"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  LOCATIONS,
  LOCATION_LIST,
  getLocationIdForDate,
  locationDaysLabel,
  type BallancerLocationId,
} from "@lib/ballancerLocations";

const PREFERRED_TIMES = ["10:00AM", "11:00AM", "12:00PM"];

/** Must match BALLANCER_HORIZON_DAYS server-side. */
const HORIZON_DAYS = parseInt(process.env.NEXT_PUBLIC_BALLANCER_HORIZON_DAYS ?? "56", 10);

/**
 * Must match BALLANCER_FAR_HORIZON_DAYS server-side. Only sizes how far the
 * month arrows page before the batch response lands; after that the server's own
 * `farTo` is used, so this cannot quietly become a second copy of the rule.
 */
const FAR_HORIZON_DAYS = parseInt(process.env.NEXT_PUBLIC_BALLANCER_FAR_HORIZON_DAYS ?? "365", 10);

/** How long a fetched availability snapshot is trusted before we re-ask. */
const SLOTS_TTL_MS = 60 * 1000;

const toMonthStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const toISODate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

type VerifyResult = {
  valid: true;
  blockBookingId: number;
  packageId: string;
  packageLabel: string;
  sessionsTotal: number;
  sessionsUsed: number;
  sessionsRemaining: number;
  personName: string;
  /** Last bookable date for time-limited packages; null when the block never lapses. */
  expiresOn: string | null;
} | { valid: false; reason: string };

export default function MySessionsClient() {
  const [step, setStep] = useState<"verify" | "booking" | "success">("verify");

  // Verify form
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [blockInfo, setBlockInfo] = useState<Extract<VerifyResult, { valid: true }> | null>(null);

  // Calendar state
  const bounds = useRef<{
    min: Date; max: Date; farMax: Date; currentMonth: string;
  } | null>(null);
  if (!bounds.current) {
    const t = new Date();
    const min = new Date(t); min.setDate(t.getDate() + 1); min.setHours(0, 0, 0, 0);
    const max = new Date(min); max.setDate(min.getDate() + HORIZON_DAYS);
    // A 10-session block is spread over months, so the calendar pages past the
    // cached horizon. Those days are checked one at a time when clicked.
    const farMax = new Date(min); farMax.setDate(min.getDate() + FAR_HORIZON_DAYS);
    bounds.current = {
      min,
      max,
      farMax,
      currentMonth: toMonthStr(min),
    };
  }
  const { min: minDate, max: maxDate, farMax, currentMonth } = bounds.current;

  /**
   * `available` — confirmed free by the batch horizon fetch.
   * `far` — past the cached horizon, so a candidate on the rota whose slots are
   *   only known once it is clicked. Offered, but never quick-booked: we have not
   *   checked Nicola's calendar for it yet.
   */
  type DayStatus = "loading" | "available" | "far" | "none";
  const [calendarMonth, setCalendarMonth] = useState(currentMonth);
  const [dayStatus, setDayStatus] = useState<Record<string, DayStatus>>({});
  /** Server's outer booking bound, once the batch fetch has reported it. */
  const [farTo, setFarTo] = useState<string | null>(null);
  const batchSlotsCache = useRef<Record<string, string[]>>({});
  const batchFetchedAt = useRef(0);
  const slotsCache = useRef<Record<string, { slots: string[]; at: number }>>({});
  const pendingQuickSlotRef = useRef<string | null>(null);
  const selectedSlotRef = useRef<string | null>(null);
  const [slotsNonce, setSlotsNonce] = useState(0);

  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  // Block clients pick their remaining sessions here across weeks. There is no location
  // PICKER on this page — they have already been to one of the sites — but the rota means
  // a Sunday and a Thursday are different places, so every day they choose has to say
  // which one, rather than letting them assume "the usual".
  const locationByDay = useRef<Record<string, BallancerLocationId>>({});
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  /** Set when the day could not be CHECKED, which is not the same as having no slots. */
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [selectedSlotRaw, setSelectedSlotRaw] = useState<string | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(null);
  const [quickBookClicked, setQuickBookClicked] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successInfo, setSuccessInfo] = useState<{ sessionNumber: number; sessionsTotal: number } | null>(null);

  // Fetch the whole booking horizon once, on entering the booking step. Was
  // per-month, which cost three sequential Graph calls each time the visitor
  // paged the calendar.
  useEffect(() => {
    if (step !== "booking") return;

    const toMark: string[] = [];
    for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
      toMark.push(toISODate(d));
    }
    if (toMark.length === 0) return;

    // A time-limited block cannot be booked past its window, so those days are
    // never offered — better than letting someone pick one and be refused by the
    // server check.
    const lastDay = blockInfo?.expiresOn ?? null;

    // Days past the cached horizon, to the server's outer bound. Availability
    // for these is NOT in the batch, so they are marked from the rota alone —
    // every day that runs at a site is a candidate, and clicking one triggers
    // the single-day lookup that says what is actually free.
    const markFar = (outerBound: string) => {
      const out: Record<string, DayStatus> = {};
      for (let d = new Date(maxDate), i = 0; i < FAR_HORIZON_DAYS; i++) {
        d.setDate(d.getDate() + 1);
        const iso = toISODate(d);
        if (iso > outerBound) break;
        if (lastDay && iso > lastDay) break;
        const locationId = getLocationIdForDate(iso);
        if (!locationId) continue;  // closed that weekday
        locationByDay.current[iso] = locationId;
        out[iso] = "far";
      }
      return out;
    };

    setDayStatus(Object.fromEntries(toMark.map((d) => [d, "loading" as DayStatus])));

    let cancelled = false;
    fetch("/api/body-ballancer/available-days", { method: "POST" })
      .then((r) => r.json())
      .then(({ availableDays, slotsByDay, locationByDay: locs, farTo: serverFarTo }: {
        availableDays: string[];
        slotsByDay: Record<string, string[]>;
        locationByDay: Record<string, BallancerLocationId>;
        farTo?: string;
      }) => {
        if (cancelled) return;
        if (locs) locationByDay.current = { ...locs, ...locationByDay.current };
        const available = new Set(availableDays ?? []);
        const outerBound = serverFarTo ?? toISODate(farMax);
        setFarTo(outerBound);
        setDayStatus({
          ...Object.fromEntries(
            toMark.map((d) =>
              [d, available.has(d) && (!lastDay || d <= lastDay) ? "available" : "none"] as const,
            ),
          ),
          ...markFar(outerBound),
        });
        if (slotsByDay) {
          for (const [day, slots] of Object.entries(slotsByDay)) {
            batchSlotsCache.current[day] = slots;
          }
        }
        batchFetchedAt.current = Date.now();
        // Land them on the first CONFIRMED free day, never a far candidate — no
        // point opening the calendar on a month whose slots we have not checked.
        const firstFree = (availableDays ?? []).find(
          (d) => toMark.includes(d) && (!lastDay || d <= lastDay),
        );
        if (firstFree) setCalendarMonth(firstFree.slice(0, 7));
      })
      .catch(() => {
        if (cancelled) return;
        // The batch is the horizon only, so a failure there says nothing about
        // the far days — they are rota-derived and still offerable.
        const outerBound = toISODate(farMax);
        setFarTo(outerBound);
        setDayStatus({
          ...Object.fromEntries(toMark.map((d) => [d, "none" as DayStatus])),
          ...markFar(outerBound),
        });
      });

    return () => { cancelled = true; };
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { selectedSlotRef.current = selectedSlotRaw; }, [selectedSlotRaw]);

  // Load slots for selected day.
  //
  // Stale-while-revalidate: whatever we already know renders immediately so
  // picking a date never blanks the list, and a background re-check corrects it
  // if anything was taken since. Only a never-fetched day shows a spinner.
  useEffect(() => {
    if (!selectedDay) return;
    setSelectedSlotRaw(null);
    setSelectedStartTime(null);
    setSlotsError(null);

    const pendingSlot = pendingQuickSlotRef.current;
    pendingQuickSlotRef.current = null;

    const applyPending = (slots: string[]) => {
      if (pendingSlot && slots.includes(pendingSlot)) applySlot(pendingSlot);
    };

    const entry = slotsCache.current[selectedDay];
    const cached = entry?.slots ?? batchSlotsCache.current[selectedDay];
    const cachedAt = entry?.at ?? batchFetchedAt.current;
    const isFresh = cached !== undefined && Date.now() - cachedAt < SLOTS_TTL_MS;

    if (cached !== undefined) {
      setAvailableSlots(cached);
      applyPending(cached);
      if (isFresh) return;
    } else {
      setAvailableSlots([]);
      setSlotsLoading(true);
    }

    let cancelled = false;

    fetch("/api/body-ballancer/availability", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ date: selectedDay }),
    })
      .then(async (r) => ({ ok: r.ok, data: await r.json() }))
      .then(({ ok, data }) => {
        if (cancelled) return;

        // A far day the server could not check answers 503, not an empty list.
        // Caching that as "no slots" would remember a Graph hiccup for the whole
        // TTL, so it is neither cached nor shown as a fully booked day.
        if (!ok) {
          setAvailableSlots([]);
          setSlotsError(typeof data?.error === "string"
            ? data.error
            : "Could not check that date — please try again.");
          return;
        }

        const slots = Array.isArray(data.slots) ? data.slots : [];
        slotsCache.current[selectedDay] = { slots, at: Date.now() };
        batchSlotsCache.current[selectedDay] = slots;
        setAvailableSlots(slots);
        if (cached === undefined) {
          applyPending(slots);
        } else if (selectedSlotRef.current && !slots.includes(selectedSlotRef.current)) {
          setSelectedSlotRaw(null);
          setSelectedStartTime(null);
        }
      })
      .catch(() => { if (!cancelled && cached === undefined) setAvailableSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });

    return () => { cancelled = true; };
  }, [selectedDay, slotsNonce]);

  function applySlot(raw: string) {
    const match = raw.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);
    if (!match) return;
    let h = parseInt(match[1], 10);
    const mins = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    else if (period === "AM" && h === 12) h = 0;
    setSelectedSlotRaw(raw);
    setSelectedStartTime(`${h.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`);
    setQuickBookClicked(null);
  }

  function handleQuickBook(day: string, slot: string) {
    if (quickBookClicked === `${day}|${slot}`) return;
    setQuickBookClicked(`${day}|${slot}`);
    pendingQuickSlotRef.current = slot;
    setCalendarMonth(day.substring(0, 7));
    setSelectedDay(day);
  }

  function resetCalendar() {
    setSelectedDay(null);
    setAvailableSlots([]);
    setSelectedSlotRaw(null);
    setSelectedStartTime(null);
    setQuickBookClicked(null);
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setVerifyError(null);
    setVerifying(true);
    try {
      const res = await fetch("/api/body-ballancer/verify-block", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), dob }),
      });
      const data: VerifyResult = await res.json();
      if (!data.valid) {
        const msg = data.reason === "all_sessions_used"
          ? "All sessions on this block have been used. Contact us if you'd like to book a new block."
          : data.reason === "expired"
          ? "This package had to be used within its booking window, which has now passed. Please get in touch and we'll sort it out."
          : "We couldn't find a block booking matching those details. Please check your email and date of birth.";
        setVerifyError(msg);
      } else {
        setBlockInfo(data);
        setStep("booking");
      }
    } catch {
      setVerifyError("Something went wrong. Please try again.");
    } finally {
      setVerifying(false);
    }
  }

  async function handleBookSession(e: React.FormEvent) {
    e.preventDefault();
    if (isSubmitting || !blockInfo || !selectedDay || !selectedStartTime) return;
    setIsSubmitting(true);
    try {
      const res = await fetch("/api/body-ballancer/book-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          blockBookingId: blockInfo.blockBookingId,
          email: email.trim().toLowerCase(),
          dob,
          date: selectedDay,
          startTime: selectedStartTime,
        }),
      });
      const data = await res.json().catch(() => ({}));

      // Slot went while this page was open — clear the stale view and reselect.
      // Matched on the code, not the bare 409: package_expired is a 409 too, and
      // must fall through to its own message rather than "someone else took it".
      if (data.code === "slot_unavailable") {
        slotsCache.current = {};
        batchSlotsCache.current = {};
        batchFetchedAt.current = 0;
        setSelectedSlotRaw(null);
        setSelectedStartTime(null);
        setSlotsNonce((n) => n + 1); // caches cleared above — re-fetch this day
        alert("Sorry — that time was just booked by someone else. Please choose another slot.");
        return;
      }

      if (!res.ok || !data.ok) throw new Error(data.error ?? "Booking failed");
      setSuccessInfo({ sessionNumber: data.sessionNumber, sessionsTotal: data.sessionsTotal });
      setStep("success");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const monthIsLoading = Object.values(dayStatus).some((s) => s === "loading");
  const availableDaysInMonth = Object.keys(dayStatus)
    .filter((d) => d.startsWith(calendarMonth)
      && (dayStatus[d] === "available" || dayStatus[d] === "far"))
    .sort();
  /** How far the month arrows go: the server's bound once known. */
  const farMaxMonth = (farTo ?? toISODate(farMax)).slice(0, 7);
  const calendarMonthLabel = (() => {
    const [y, m] = calendarMonth.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
  })();
  const quickBookOptions: { day: string; slot: string; dayLabel: string; timeLabel: string }[] = [];
  {
    const availableDays = Object.keys(dayStatus).filter((d) => dayStatus[d] === "available").sort().slice(0, 14);
    for (const day of availableDays) {
      const cached = batchSlotsCache.current[day] ?? slotsCache.current[day]?.slots;
      if (!cached?.length) continue;
      const slot = PREFERRED_TIMES.find((t) => cached.includes(t)) ?? cached[0];
      const d = new Date(day + "T00:00:00");
      const dayLabel = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      quickBookOptions.push({ day, slot, dayLabel, timeLabel: slot.replace(":00", "").toLowerCase() });
      if (quickBookOptions.length >= 3) break;
    }
  }
  const friendlyDay = (iso: string) =>
    new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  // ── Step: verify ──────────────────────────────────────────────────────────
  if (step === "verify") {
    return (
      <div className="rounded-3xl bg-[var(--bg-card)] border border-[var(--border-on-light)] shadow-sm p-6 md:p-8">
        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-on-light)] mb-1">
              Email address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="The email you used when you paid"
              className="w-full rounded-xl border border-[var(--border-on-light)] px-3 py-2 text-sm text-[var(--text-on-light)] bg-[var(--bg-surface)] focus:outline-none focus:ring-2 focus:ring-[var(--text-brand)]/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-on-light)] mb-1">
              Date of birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="w-full sm:w-48 rounded-xl border border-[var(--border-on-light)] px-3 py-2 text-sm text-[var(--text-on-light)] bg-[var(--bg-surface)] focus:outline-none focus:ring-2 focus:ring-[var(--text-brand)]/30"
            />
          </div>
          {verifyError && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{verifyError}</p>
          )}
          <button
            type="submit"
            disabled={verifying}
            className="w-full sm:w-auto rounded-full bg-[var(--text-brand)] px-8 py-3 text-sm font-semibold text-white hover:opacity-90 transition disabled:opacity-50"
          >
            {verifying ? "Checking…" : "Find my sessions"}
          </button>
        </form>
      </div>
    );
  }

  // ── Step: success ─────────────────────────────────────────────────────────
  if (step === "success" && successInfo) {
    const remaining = successInfo.sessionsTotal - successInfo.sessionNumber;
    return (
      <div className="rounded-3xl bg-[var(--bg-card)] border border-[var(--border-on-light)] shadow-sm p-6 md:p-8 text-center">
        <div className="text-4xl mb-4">✓</div>
        <h2 className="text-xl font-bold text-[var(--text-on-light)] mb-2">Session booked!</h2>
        <p className="text-sm text-[var(--text-on-light-muted)] mb-6">
          Session {successInfo.sessionNumber} of {successInfo.sessionsTotal} confirmed.
          A confirmation email is on its way to you.
        </p>
        {remaining > 0 && (
          <button
            onClick={() => {
              setStep("booking");
              resetCalendar();
              if (blockInfo) {
                setBlockInfo({ ...blockInfo, sessionsUsed: blockInfo.sessionsUsed + 1, sessionsRemaining: blockInfo.sessionsRemaining - 1 });
              }
            }}
            className="rounded-full bg-[var(--text-brand)] px-8 py-3 text-sm font-semibold text-white hover:opacity-90 transition"
          >
            Book session {successInfo.sessionNumber + 1} →
          </button>
        )}
      </div>
    );
  }

  // ── Step: booking ─────────────────────────────────────────────────────────
  return (
    <div className="rounded-3xl bg-[var(--bg-card)] border border-[var(--border-on-light)] shadow-sm overflow-hidden">

      {/* Session counter */}
      {blockInfo && (
        <div className="px-6 pt-6 pb-0 md:px-8">
          <div className="rounded-xl bg-[var(--bg-surface)] border border-[var(--border-on-light)] px-4 py-3 mb-5 flex items-center justify-between">
            <div>
              <p className="text-xs text-[var(--text-on-light-muted)] uppercase tracking-wider font-semibold">Your block</p>
              <p className="text-sm font-semibold text-[var(--text-on-light)] mt-0.5">{blockInfo.packageLabel}</p>
              {blockInfo.expiresOn && (
                <p className="text-xs text-[var(--text-on-light-muted)] mt-1">
                  Book by {friendlyDay(blockInfo.expiresOn)}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-[var(--text-on-light-muted)]">Sessions remaining</p>
              <p className="text-2xl font-bold text-[var(--text-brand)]">{blockInfo.sessionsRemaining}</p>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 md:p-8">

        {/* Quick book strip */}
        {(monthIsLoading && quickBookOptions.length === 0) ? (
          <div className="mb-6">
            <p className="text-xs font-semibold text-[var(--text-on-light-muted)] uppercase tracking-wider mb-2">Next available</p>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-[58px] w-24 rounded-2xl bg-[var(--bg-surface)] animate-pulse" />)}
            </div>
          </div>
        ) : quickBookOptions.length > 0 ? (
          <div className="mb-6">
            <p className="text-xs font-semibold text-[var(--text-on-light-muted)] uppercase tracking-wider mb-2">Next available</p>
            <div className="flex flex-wrap gap-2">
              {quickBookOptions.map(({ day, slot, dayLabel, timeLabel }) => {
                const isActive = (selectedDay === day && selectedSlotRaw === slot) || quickBookClicked === `${day}|${slot}`;
                const isDisabled = quickBookClicked !== null && quickBookClicked !== `${day}|${slot}`;
                return (
                  <button
                    key={`${day}-${slot}`}
                    type="button"
                    onClick={() => handleQuickBook(day, slot)}
                    disabled={isDisabled}
                    className={["flex flex-col items-center px-4 py-2.5 rounded-2xl text-sm border transition-all",
                      isDisabled ? "border-[var(--border-on-light)] text-[var(--text-on-light-muted)] cursor-not-allowed opacity-40"
                        : isActive ? "bg-[var(--text-brand)] border-[var(--text-brand)] text-white shadow-md"
                        : "bg-[var(--bg-surface)] border-[var(--border-on-light)] text-[var(--text-brand)] hover:border-[var(--text-brand)] hover:shadow-sm",
                    ].join(" ")}
                  >
                    <span className="font-semibold leading-tight">{timeLabel}</span>
                    <span className={["text-xs leading-tight mt-0.5", isActive ? "text-white/80" : "text-[var(--text-on-light-muted)]"].join(" ")}>{dayLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-[var(--border-on-light)]" />
          <span className="text-xs text-[var(--text-on-light-muted)] whitespace-nowrap">or choose a date</span>
          <div className="flex-1 h-px bg-[var(--border-on-light)]" />
        </div>

        {/* Month calendar */}
        <div className="rounded-2xl bg-[var(--bg-surface)] border border-[var(--border-on-light)] p-4 mb-5">
          <div className="flex items-center justify-between mb-4">
            <button type="button"
              onClick={() => { const [y, m] = calendarMonth.split("-").map(Number); setCalendarMonth(toMonthStr(new Date(y, m - 2, 1))); resetCalendar(); }}
              disabled={calendarMonth <= currentMonth}
              className="w-8 h-8 flex items-center justify-center rounded-full text-[var(--text-on-light-muted)] text-xl disabled:opacity-20 hover:bg-[var(--border-on-light)] transition"
              aria-label="Previous month"
            >‹</button>
            <div className="text-center">
              <p className="text-sm font-semibold text-[var(--text-on-light)]">{calendarMonthLabel}</p>
              {monthIsLoading && <p className="text-[11px] text-[var(--text-on-light-muted)] animate-pulse mt-0.5">Checking availability…</p>}
            </div>
            <button type="button"
              onClick={() => { const [y, m] = calendarMonth.split("-").map(Number); setCalendarMonth(toMonthStr(new Date(y, m, 1))); resetCalendar(); }}
              disabled={calendarMonth >= farMaxMonth}
              className="w-8 h-8 flex items-center justify-center rounded-full text-[var(--text-on-light-muted)] text-xl disabled:opacity-20 hover:bg-[var(--border-on-light)] transition"
              aria-label="Next month"
            >›</button>
          </div>
          <p className="text-[11px] text-[var(--text-on-light-muted)] text-center mb-3">
            {LOCATION_LIST.map((loc) => `${locationDaysLabel(loc.id)} at ${loc.name}`).join(" · ")}
          </p>
          {monthIsLoading ? (
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-9 w-20 rounded-full bg-[var(--border-on-light)] animate-pulse" />)}
            </div>
          ) : availableDaysInMonth.length === 0 ? (
            <p className="text-sm text-[var(--text-on-light-muted)] text-center py-2">No available dates this month.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {availableDaysInMonth.map((day) => {
                const d = new Date(day + "T00:00:00");
                const label = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
                const isSelected = day === selectedDay;
                return (
                  <button key={day} type="button"
                    onClick={() => {
                      // Deliberately no setAvailableSlots([]) — the load effect renders
                      // this day's cached slots immediately, and clears them itself when
                      // nothing is cached. Blanking here forces an empty frame per click.
                      setSelectedDay(day);
                      setSelectedSlotRaw(null);
                      setSelectedStartTime(null);
                      setQuickBookClicked(null);
                    }}
                    className={["px-3 py-1.5 rounded-full text-sm font-medium border transition-all",
                      isSelected ? "bg-[var(--text-brand)] border-[var(--text-brand)] text-white shadow-md"
                        : "bg-[var(--bg-card)] border-[var(--border-on-light)] text-[var(--text-brand)] hover:border-[var(--text-brand)] hover:shadow-sm",
                    ].join(" ")}
                  >{label}</button>
                );
              })}
            </div>
          )}
        </div>

        {/* Time slots */}
        {selectedDay && (
          slotsLoading ? (
            <p className="text-sm text-[var(--text-brand)] animate-pulse mb-5">Loading times…</p>
          ) : availableSlots.length > 0 ? (
            <div className="mb-5">
              <p className="text-sm font-medium text-[var(--text-on-light)] mb-1">{friendlyDay(selectedDay)}</p>
              {locationByDay.current[selectedDay] && (
                <p className="text-xs text-[var(--text-on-light-muted)] mb-3">
                  at {LOCATIONS[locationByDay.current[selectedDay]!].name}, {LOCATIONS[locationByDay.current[selectedDay]!].area}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {availableSlots.map((slot) => (
                  <button key={slot} type="button" onClick={() => applySlot(slot)} aria-pressed={selectedSlotRaw === slot}
                    className={["px-4 py-2 rounded-full text-sm font-medium border transition-all",
                      selectedSlotRaw === slot ? "bg-[var(--text-brand)] border-[var(--text-brand)] text-white shadow-md"
                        : "bg-[var(--bg-card)] border-[var(--border-on-light)] text-[var(--text-brand)] hover:border-[var(--text-brand)]",
                    ].join(" ")}
                  >{slot}</button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
              {slotsError ?? "No times available on this day — please pick another date."}
            </p>
          )
        )}

        {/* Confirm */}
        {selectedSlotRaw && selectedDay && blockInfo && (
          <form onSubmit={handleBookSession}>
            <div className="rounded-xl bg-[var(--bg-surface)] border border-[var(--border-on-light)] px-4 py-3 mb-5 text-sm text-[var(--text-on-light-muted)] space-y-1">
              <p><span className="text-[var(--text-on-light)] font-medium">Date:</span> {friendlyDay(selectedDay)}</p>
              <p><span className="text-[var(--text-on-light)] font-medium">Time:</span> {selectedSlotRaw} (45 minutes)</p>
              {locationByDay.current[selectedDay] && (
                <p><span className="text-[var(--text-on-light)] font-medium">Location:</span> {LOCATIONS[locationByDay.current[selectedDay]!].name}, {LOCATIONS[locationByDay.current[selectedDay]!].area}</p>
              )}
              <p><span className="text-[var(--text-on-light)] font-medium">Session:</span> {blockInfo.sessionsUsed + 1} of {blockInfo.sessionsTotal}</p>
            </div>
            <button type="submit" disabled={isSubmitting}
              className="w-full sm:w-auto rounded-full bg-[var(--text-brand)] px-10 py-3 text-sm font-semibold text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Booking…" : "Confirm session"}
            </button>
            <p className="mt-3 text-xs text-[var(--text-on-light-muted)]">
              No payment needed — this session is covered by your block. A calendar invite and confirmation email follow automatically.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
